import {useAuthStore, isWidgetContext} from "~/stores/auth";
import {jwtDecode} from "@/utils/jwtDecode";
import type {TLoginResponce} from "~/types/user";
import { ofetch } from 'ofetch'
import type {NitroFetchRequest} from 'nitropack';

// Основной сайт: токен живёт в httpOnly-cookie, JS его не видит. Момент рефреша
// определяем по exp, сохранённому из ответа логина/рефреша (authSession в localStorage).
// Widget-контекст (iframe на сайте клиники): cookie third-party и не отправляется,
// поэтому токен хранится в localStorage и уходит заголовком AuthToken (useApi).
export default defineNuxtPlugin((nuxtApp) => {
    const authStore = useAuthStore(nuxtApp.$pinia);

    let promise: Promise<void> | null = null;
    let isRefreshing = false;

    const {apiQuery} = useApi();
    const route = useRoute();
    const router = useRouter();

    const sessionExpiresAt = (): number | undefined => {
        if (isWidgetContext()) {
            const token = authStore.accessToken;
            if (!token) return undefined;
            try {
                const exp = jwtDecode(token).exp;
                return exp ? exp * 1000 : undefined;
            } catch {
                return undefined;
            }
        }
        return authStore.sessionExp ? authStore.sessionExp * 1000 : undefined;
    };

    const refreshTokenIfNeeded = async () => {
        const expiresAt = sessionExpiresAt();

        if (expiresAt && Date.now() >= expiresAt) {
            if (!isRefreshing) {
                isRefreshing = true;
                promise = updateTokenQuery().finally(() => {
                  isRefreshing = false;
                });
            }
        }
    };
    const updateTokenQuery = async () => {
        return apiQuery<TLoginResponce>("/auth/refresh/", "POST", {})
        .then(response => {
            setTokenData(response);
        })
        .catch(error => {
            authStore.setAccessToken(null);
            if(route.path.includes('profile')) {
                router.push({path: '/'});
            }
        })
    }

    const setTokenData = (data: TLoginResponce) => {
        // setAccessToken сам решает: widget — хранит токен, основной сайт —
        // только exp+роль (токен остаётся лишь в httpOnly-cookie).
        authStore.setAccessToken(data.accessToken);
        try {
            const roleMain = jwtDecode(data.accessToken)?.role;
            if (roleMain) {
                authStore.setUserRole(roleMain);
            }
        } catch {}
    }
    if (process.client) {
        if (route.query.auth === 'admin') {
            try {
                localStorage.removeItem('accessToken');
                localStorage.removeItem('authSession');
                updateTokenQuery();
            } catch (error) {
                console.error('Token refresh failed:', error)
            }
        }

        if (isWidgetContext()) {
            const token = localStorage.getItem('accessToken');
            if (token) {
                authStore.setAccessToken(token);
                try {
                    const tokenData = jwtDecode(token);
                    const iss = tokenData?.iss;
                    let roleMain = tokenData?.role;
                    if (roleMain) {
                        authStore.setUserRole(roleMain);
                    } else if (iss) {
                        const regex = /\/v1\/(client|clinic|doctor)\//;
                        const role = iss.match(regex);
                        if(role && role[1]) {
                            authStore.setUserRole(role[1]);
                        }
                    }
                } catch {}
            }
        } else {
            // Миграция со старой схемы: токен из localStorage переносим в
            // authSession (cookie у пользователя уже стоит — её ставил бэкенд
            // при каждом логине/рефреше) и удаляем из localStorage.
            const legacyToken = localStorage.getItem('accessToken');
            if (legacyToken) {
                authStore.setAccessToken(legacyToken);
            } else {
                authStore.restoreSession();
            }

            // Сессия по cookie могла «протухнуть», пока вкладка была закрыта —
            // тогда сразу рефрешим (refresh_ttl две недели против часа у access).
            const expiresAt = sessionExpiresAt();
            if (expiresAt && Date.now() >= expiresAt) {
                refreshTokenIfNeeded();
            }
        }

        globalThis.$fetch = ofetch.create({
            async onRequest (req) {
                if(!req.request.toString().includes('auth/refresh')) {
                    refreshTokenIfNeeded();
                    if (promise) {
                        await promise.then(() => {
                            // Основной сайт: после рефреша обновлённая cookie уже
                            // у браузера, заголовки не нужны. Widget: свежий токен
                            // подставит useApi из стора.
                            promise = null;
                        });
                    }
                }
            },
            onRequestError ({ error }) {
            }
        }) as $Fetch<unknown, NitroFetchRequest>
    }

    return {
        provide: {
            refreshTokenIfNeeded
        }
    }
})
